/** Who is signed in, the theme, and the way out. */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import {
  dialog,
  resetApiState,
  selectTheme,
  selectUser,
  signedOut,
  themeChosen,
  useLogoutMutation,
  type ThemeChoice,
} from '@pos/shared';

import { useAppDispatch, useAppSelector } from '@/app/hooks';

const THEMES: readonly { value: ThemeChoice; label: string }[] = [
  { value: 'system', label: 'Match the system' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

export function AccountMenu() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const theme = useAppSelector(selectTheme);
  const [logout] = useLogoutMutation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const signOut = async () => {
    setAnchor(null);

    const ok = await dialog.confirm({
      title: 'Sign out?',
      body: `${user?.fullName ?? 'You'} will be signed out of the dashboard on this device. The till app is a separate sign-in and is not affected.`,
      confirmLabel: 'Sign out',
      tone: 'danger',
    });

    if (!ok) return;

    void logout();
    dispatch(signedOut());
    dispatch(resetApiState());
    void navigate('/sign-in', { replace: true });
  };

  return (
    <>
      {/*
        Avatar, name and role in one target rather than a bare avatar: on a shared shop terminal
        the question "who is this signed in as" gets asked constantly, and answering it should not
        require opening a menu. Collapses to the avatar alone on a phone.
      */}
      <ButtonBase
        data-tour="account"
        onClick={(event) => {
          setAnchor(event.currentTarget);
        }}
        aria-label="Account"
        aria-haspopup="menu"
        sx={{
          gap: 1.25,
          pl: 0.5,
          pr: { xs: 0.5, sm: 1 },
          py: 0.5,
          borderRadius: 'var(--pos-radius-chip)',
          '&:hover': { bgcolor: 'var(--pos-surface-sunken)' },
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: 13,
            fontWeight: 700,
            bgcolor: 'var(--pos-brand-subtle)',
            color: 'var(--pos-brand-subtle-text)',
          }}
        >
          {initials(user?.fullName)}
        </Avatar>

        <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'left', minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.25 }} noWrap>
            {user?.fullName ?? '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }} noWrap>
            {user?.roleName ?? ''}
          </Typography>
        </Box>

        <ExpandMoreIcon
          sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 18, color: 'text.secondary' }}
        />
      </ButtonBase>

      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => {
          setAnchor(null);
        }}
        slotProps={{ paper: { sx: { minWidth: 240 } } }}
      >
        <MenuItem disabled sx={{ opacity: '1 !important' }}>
          <ListItemText
            primary={user?.fullName ?? '—'}
            secondary={`${user?.roleName ?? ''} · ${user?.homeShopName ?? ''}`}
            slotProps={{ primary: { fontWeight: 700 } }}
          />
        </MenuItem>

        <Divider />

        {THEMES.map((option) => (
          <MenuItem
            key={option.value}
            selected={theme === option.value}
            onClick={() => {
              dispatch(themeChosen(option.value));
              setAnchor(null);
            }}
          >
            {option.label}
          </MenuItem>
        ))}

        <Divider />

        <MenuItem onClick={() => void signOut()}>
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
}

function initials(name: string | undefined): string {
  if (name === undefined || name.length === 0) return '–';

  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';

  return `${first}${last}`.toUpperCase();
}
